import type { BadgeTone } from '@/components/ui';
import type {
  NotificationCatalogue,
  NotificationChannel,
  NotificationDecision,
  NotificationEventInfo,
} from '@/lib/types';

/** One label and tone per send-log decision. The only copy of this wording. */
export const OUTCOMES: Record<NotificationDecision, { label: string; tone: BadgeTone }> = {
  sent: { label: 'Sent', tone: 'success' },
  queued_digest: { label: 'Daily summary', tone: 'info' },
  grouped: { label: 'Grouped', tone: 'info' },
  suppressed_pref: { label: 'Switched off', tone: 'neutral' },
  suppressed_actor: { label: 'Own action', tone: 'neutral' },
  suppressed_access: { label: 'No access', tone: 'warning' },
  suppressed_paused: { label: 'Paused', tone: 'neutral' },
  throttled: { label: 'Throttled', tone: 'warning' },
  skipped_no_address: { label: 'No address', tone: 'warning' },
  failed: { label: 'Failed', tone: 'danger' },
};

export const DECISIONS = Object.keys(OUTCOMES) as NotificationDecision[];

export function isDecision(value: string | null | undefined): value is NotificationDecision {
  return value != null && Object.prototype.hasOwnProperty.call(OUTCOMES, value);
}

export const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  email: 'Email',
  inbox: 'In-app',
};

/** entity_type (plural, as the server writes it) → the record page base path. */
const RECORD_ROUTES: Record<string, string> = {
  companies: '/companies',
  contacts: '/contacts',
  deals: '/deals',
  quotes: '/quotes',
  invoices: '/invoices',
  credit_notes: '/credit-notes',
  'credit-notes': '/credit-notes',
  projects: '/projects',
  marketing_campaigns: '/marketing/campaigns',
};

/** The app route for a logged record, or null when there is no page for it. */
export function recordPath(entityType: string | null, entityId: number | null): string | null {
  if (!entityType || entityId === null) return null;
  const base = RECORD_ROUTES[entityType];
  return base ? `${base}/${entityId}` : null;
}

export interface CatalogueEventEntry {
  event: NotificationEventInfo;
  moduleKey: string;
  moduleLabel: string;
  submoduleLabel: string;
}

/** The catalogue flattened in its own order: module → submodule → event. */
export function flattenCatalogue(catalogue: NotificationCatalogue | null): CatalogueEventEntry[] {
  if (!catalogue) return [];
  const out: CatalogueEventEntry[] = [];
  for (const module of catalogue.modules) {
    for (const submodule of module.submodules) {
      for (const event of submodule.events) {
        out.push({
          event,
          moduleKey: module.key,
          moduleLabel: module.label,
          submoduleLabel: submodule.label,
        });
      }
    }
  }
  return out;
}
